/**
 * @brisa/ui · AuthLayout
 *
 * Wrapper de telas auth (facelift v1). Layout 2-column desktop (form ESQUERDA,
 * brand panel DIREITA, ambos 50% width) / 1-column mobile (brand panel oculto
 * via `display: none` <1024px). O canvas inteiro (incluindo a form column)
 * vive em gradient sage `--bg-canvas-gradient` — match exato do Figma 223:787
 * (file 4kRRaXDq85zVVXByHGEFfY).
 *
 * Reconciliação Figma vs UI_SPEC v1.2: ver
 * docs/ui-specs/admin-basico-login/UI_SPEC.md §2.6 (changelog v1.3).
 *
 * Form column:
 *   - Container max-width 440px centralizado (vertical+horizontal).
 *   - Padding lateral 24px mobile / 48px desktop, padding-y 48px / 96px.
 *   - O brand mark canônico do Figma (SquiggleLogo amarelo 40×39 no topo do
 *     container) **é responsabilidade do children** — não do wrapper. Isso
 *     preserva a hierarquia exata do Figma onde o brand mark é a primeira
 *     coisa do form container, sem header wrapper extra.
 *
 * Props legacy (`showBrand`, `brandSize`):
 *   - Mantidos para retrocompat das páginas auth pré-facelift
 *     (register, reset-password, verify-email-sent, verify-email, consent, error).
 *   - Quando `showBrand=true`, renderiza um header com o `<BrandLogo>` textual
 *     legacy ANTES do children — para essas páginas continuarem com o look
 *     wordmark "Brisa Pass" até serem migradas para o squiggle yellow do
 *     facelift v1 (uma a uma, em features futuras).
 *   - Páginas novas (login redesign W3.2) devem passar `showBrand={false}`
 *     e incluir o `<SquiggleLogo>` no topo do próprio children.
 *
 * Brand column:
 *   - 50% width, min-height 100vh, overflow hidden, gradient sage como
 *     fallback enquanto a mídia carrega.
 *   - Default: `<LoginBrandPanel />` (vídeo aleatório do manifesto `/login-media.json`).
 *   - Passe `brandPanel={null}` para variantes futuras (erro, callback, etc.).
 *
 * Acessibilidade:
 *   - `<main>` semântico com id="main-content".
 *   - `<aside aria-hidden="true">` no brand panel (decorativo).
 *   - Skip link "Saltar para o conteúdo" removido em 2026-05-23 a pedido do PL —
 *     UX over a11y trade-off consciente em telas auth (poucos elementos
 *     focáveis no fluxo: brand → banner → form → submit → footer link).
 *     TODO a11y: se voltarmos atrás, re-adicionar com classe sr-only +
 *     :focus-visible visível (padrão WCAG 2.4.1). Como `.brisa-skip-link` não
 *     tinha regra CSS implementada nos pacotes reais (apenas em prototypes),
 *     o link aparecia visível no topo da página — daí a remoção urgente.
 *
 * Pilares: P1 (entrar na cena — mídia documental, não squiggle decorativo)
 *          + P2 (App generosa — densidade do form respira)
 *          + P3 (escolhas poucas — gradient sage único; amarelo único no CTA).
 */
import * as React from 'react';
import { BrandLogo } from './brand-logo';
import { LoginBrandPanel } from './components/facelift/login-brand-panel/LoginBrandPanel';

export interface AuthLayoutProps {
  children: React.ReactNode;
  /**
   * Renderiza o `<BrandLogo>` textual legacy no topo da form col (header
   * wrapper extra). Default `true` para retrocompat. Passe `false` para
   * páginas no design facelift v1 (Figma 223:787) que já incluem o
   * `<SquiggleLogo>` no topo do próprio children.
   */
  showBrand?: boolean;
  /**
   * Tamanho do BrandLogo legacy (quando `showBrand=true`). Ignorado quando
   * `showBrand=false`.
   */
  brandSize?: 'sm' | 'md';
  /**
   * Painel decorativo à direita (desktop ≥1024px, oculto mobile).
   * Default: `<LoginBrandPanel />` (sage gradient + vídeo aleatório do manifest
   * `/login-media.json`). Passe `null` para desligar em variantes sem brand
   * panel (ex: callback OAuth, erro full-screen).
   */
  brandPanel?: React.ReactNode | null;
}

export function AuthLayout({
  children,
  showBrand = true,
  brandSize = 'md',
  // footerLabel={null} per decisão PL 2026-05-23 W3.2: o texto "BRISA PASS ·
  // BARCELONA 2026" sobre o vídeo competia visualmente com a mídia documental.
  // Mantém o LoginBrandPanel (gradient + vídeo) sem o overlay textual.
  brandPanel = <LoginBrandPanel footerLabel={null} />,
}: AuthLayoutProps) {
  return (
    <main id="main-content" className="brisa-auth-layout">
      <div className="brisa-auth-layout__form-col">
        <div className="brisa-auth-layout__container">
          {showBrand ? (
            <header className="brisa-auth-layout__header">
              <BrandLogo size={brandSize} />
            </header>
          ) : null}
          {children}
        </div>
      </div>
      {brandPanel ? (
        <aside className="brisa-auth-layout__brand-col" aria-hidden="true">
          {brandPanel}
        </aside>
      ) : null}
    </main>
  );
}
